import { CustomValidatedDefaultBid } from '@bingads-webui-campaign-react/default-bid';
import { Alert } from '@bingads-webui-react/alert';
import PropTypes from 'prop-types';
import React, { useCallback, useMemo, useState } from 'react';
import { getBidRecommendationsRange } from './bid-recommendations-data';

// Component to display bid recommendation message
const BidRecommendationInfo = ({ bidRecommendationRange, validationResult, i18n, isEdit }) => {
  const { isValidated, isValid } = validationResult;
  if (!isValidated || isValid || !bidRecommendationRange) {
    return null;
  }

  const { currencyCode } = bidRecommendationRange;
  const messageData = {
    bidFloor: i18n.formatCurrency(bidRecommendationRange.bidFloor, currencyCode),
    bidRecommendation: i18n.formatCurrency(bidRecommendationRange.bidRecommendation, currencyCode),
    bidCeil: i18n.formatCurrency(bidRecommendationRange.bidCeil, currencyCode),
  };

  const message = isEdit ?
    i18n.getString(_TL_('Use the recommended bid of {{bidRecommendation}} to improve ad delivery. A typical range for similar campaigns is between {{bidFloor}} and {{bidCeil}}'), messageData) :
    i18n.getString(_TL_('A typical range for similar campaigns is between {{bidFloor}} and {{bidCeil}}'), messageData);

  return (
    <div style={{ marginTop: '0.5em' }} data-test-id="bidRecommendationInfoMessage">
      <Alert level="Info" message={message} />
    </div>);
};

BidRecommendationInfo.propTypes = {
  bidRecommendationRange: PropTypes.shape({
    bidFloor: PropTypes.number,
    bidRecommendation: PropTypes.number,
    bidCeil: PropTypes.number,
    currencyCode: PropTypes.string,
  }),
  validationResult: PropTypes.shape({
    isValidated: PropTypes.bool,
    isValid: PropTypes.bool,
  }).isRequired,
  isEdit: PropTypes.bool.isRequired,
  i18n: PropTypes.shape({
    getString: PropTypes.func,
    formatCurrency: PropTypes.func,
  }).isRequired,
};
BidRecommendationInfo.defaultProps = {
  bidRecommendationRange: null,
};

export const useCampaignBidTypeInfo = (campaign, currencyCode) => {
  const campaignType = campaign && campaign.CampaignType;
  const campaignSubType = campaign && campaign.CampaignSubType;
  const biddingSchemeType = campaign && campaign.BiddingScheme && campaign.BiddingScheme.Type;

  return useMemo(() => ({
    campaignType,
    campaignSubType,
    biddingSchemeType,
    currencyCode,
  }), [campaignType, campaignSubType, biddingSchemeType, currencyCode]);
};

// Hook to get the BidRecommendationRange data & a validator function
export const useBidRecommendation = (campaignBidTypeInfo) => {
  const bidRecommendationRange = useMemo(() => getBidRecommendationsRange(campaignBidTypeInfo), [campaignBidTypeInfo]);
  const checkIsBidInRecommendedRange = useCallback((bidValue) => {
    if (!bidRecommendationRange || !bidValue) { // Unknown range should show no message
      return { isValidated: false, isValid: false };
    }

    const isValid = bidValue >= bidRecommendationRange.bidFloor;
    return {
      isValidated: true,
      isValid,
    };
  }, [bidRecommendationRange]);

  return [bidRecommendationRange, checkIsBidInRecommendedRange];
};

/*
 * HOC to wrap a component with BidRecommendation data & message banner
 */
export const withBidRecommendation = (Component) => {
  const BidRecommendationWrappedComponent = (props) => {
    const { campaignBidTypeInfo, i18n, isEdit, ...rest } = props;

    /**
     * Lookup the bidRecommendationRange
     */
    const [bidRecommendationRange, checkIsBidInRecommendedRange] = useBidRecommendation(campaignBidTypeInfo);
    const [validationResult, setValidationResult] = useState({ isValidated: false, isValid: false });

    /**
     * Callback to listen to bid value changes
     */
    const onBidChange = useCallback((nextValue) => {
      const nextValidationResult = checkIsBidInRecommendedRange(nextValue);
      setValidationResult(nextValidationResult);
    }, [checkIsBidInRecommendedRange]);

    // NOTE: do NOT set `validationProps.data` here - it gets out of sync with the actual viewModel being used to save.

    return (
      <>
        <Component {...rest} i18n={i18n} onBidChange={onBidChange} />
        <BidRecommendationInfo
          bidRecommendationRange={bidRecommendationRange}
          validationResult={validationResult}
          i18n={i18n}
          isEdit={isEdit}
        />
      </>
    );
  };

  BidRecommendationWrappedComponent.propTypes = {
    campaignBidTypeInfo: PropTypes.shape({
      campaignType: PropTypes.string.isRequired,
      campaignSubType: PropTypes.string.isRequired,
      biddingSchemeType: PropTypes.string.isRequired,
      currencyCode: PropTypes.string.isRequired,
    }).isRequired,
    i18n: PropTypes.shape({
      getString: PropTypes.func,
      formatCurrency: PropTypes.func,
    }).isRequired,
    isEdit: PropTypes.bool.isRequired,

    // Props for <CustomValidatedDefaultBid />
    validationProps: PropTypes.shape({
      field: PropTypes.string.isRequired,
      data: PropTypes.shape({}).isRequired,
    }).isRequired,
  };

  return BidRecommendationWrappedComponent;
};

export const DefaultBidWithRecommendation = withBidRecommendation(CustomValidatedDefaultBid);
